import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Empty, Popup, Toast } from 'antd-mobile';
import {
  ClockCircleOutline,
  CheckCircleOutline,
  ExclamationCircleOutline,
  RightOutline,
  CalendarOutline,
  StarFill,
} from 'antd-mobile-icons';
import styled, { keyframes, css } from 'styled-components';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  selectCurrentScooter,
  fetchUserScooters as fetchScootersAction,
  setSelectedScooter,
} from '../store/slices/scooterSlice';
import {
  fetchWalletBalance,
  selectWalletBalance,
} from '../store/slices/walletSlice';
import { selectUserType } from '../store/slices/authSlice';
import { packService, scooterPackService, walletService } from '../services/api';
import type {
  PackHistoryCounts,
  PackHistoryItem,
  PackHistoryKind,
  PackHistoryResponse,
  PackHistoryStatusFilter,
} from '../services/api/types';
import AutoActivationToggle from '../components/molecules/AutoActivationToggle';
import QueuedPackActions from '../components/molecules/QueuedPackActions';
import EnergyPackPurchaseModal from '../components/organisms/EnergyPackPurchaseModal';
import ScooterPackPurchaseModal from '../components/organisms/ScooterPackPurchaseModal';
import { invalidateScooterReadiness } from '../hooks/useScooterReadiness';
import { isFleetType } from '../types/user';
import { formatAh } from '../services/api/numberUtils';
import { activateQueuedPack, canManuallyActivatePack } from '../utils/manualPackActivation';
import { formatRemainingHours, formatValidityHours } from '../utils/packValidity';
import { r } from '../theme/riderTheme';
import {
  PageHeader,
  PageHeaderText,
  PageHeaderEyebrow,
  PageHeaderTitle,
  PrimaryButton,
  ButtonChevron,
} from '../theme/surfacePrimitives';
import { CloseButton } from '../theme/sheetPrimitives';

const EMPTY_COUNTS: PackHistoryCounts = {
  all: 0,
  active: 0,
  queued: 0,
  expired: 0,
};

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  width: 100%;
`;

// Segmented control for Energy / Scooter
const KindSegmented = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 0 0 14px;
`;

const KindSegment = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  min-height: 44px;
  border-radius: 10px;
  padding: 10px 12px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease,
    box-shadow 200ms ease;

  ${({ theme, $active }) =>
    $active
      ? css`
          background: ${r(theme).surface.raisedSelected};
          color: ${r(theme).text.primary};
          border: 1px solid ${r(theme).text.primary};
          box-shadow: 0 0 0 3px ${r(theme).glow.selectionLime};
        `
      : css`
          background: ${r(theme).surface.sheet};
          color: ${r(theme).text.muted};
          border: 1px solid ${r(theme).surface.edge};

          &:hover {
            background: ${r(theme).surface.inputIdle};
            color: ${r(theme).text.primary};
            border-color: ${r(theme).surface.edgeStrong};
          }
        `}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

// Status filter row (All / Active / Queued / Expired)
const StatusFilters = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 0 14px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StatusFilter = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;

  ${({ theme, $active }) =>
    $active
      ? css`
          background: ${r(theme).surface.raisedSelected};
          color: ${r(theme).text.primary};
          border: 1px solid ${r(theme).text.primary};
          box-shadow: 0 0 0 3px ${r(theme).glow.selectionLime};
        `
      : css`
          background: ${r(theme).surface.inputIdle};
          color: ${r(theme).text.muted};
          border: 1px solid ${r(theme).surface.edge};

          &:hover {
            background: ${r(theme).surface.sheet};
            color: ${r(theme).text.primary};
            border-color: ${r(theme).surface.edgeStrong};
          }
        `}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }

  & > small {
    font-family: 'Geist', sans-serif;
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: none;
    color: ${({ theme }) => r(theme).text.faint};
  }
`;

const PackListContainer = styled.div`
  padding: 0;
`;

// Wrapper article — pure container. The clickable surface is the inner
// PackItemSurface <button> below, kept structurally separate from the
// "Set as next" button so the two controls don't nest. SetAsNextSlot sits
// in a sibling absolute-positioned cell on top of the surface button.
const PackItem = styled.article`
  position: relative;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 14px;
  margin-bottom: 10px;
  transition: border-color 180ms ease;
  animation: ${fadeIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1);

  &:hover,
  &:focus-within {
    border-color: ${({ theme }) => r(theme).surface.edgeStrong};
  }
`;

// The full-card surface as a real <button>. Owns the "open details" affordance
// for both pointer and keyboard users.
const PackItemSurface = styled.button.attrs({ type: 'button' as const })`
  display: block;
  width: 100%;
  padding: 14px 16px 14px;
  background: transparent;
  border: 0;
  border-radius: 14px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1);

  &:active {
    transform: scale(0.995);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

// Top-right slot for "Set as next" / chevron — positioned over the surface
// button so it doesn't structurally nest inside it. The slot itself is
// pointer-transparent so the decorative chevron forwards taps to the
// underlying card button; the real action button below re-enables
// pointer events on itself.
const PackItemActionSlot = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 1;
  display: flex;
  align-items: center;
  pointer-events: none;

  & > button {
    pointer-events: auto;
  }
`;

const PackHeader = styled.div<{ $reserveActionSlot: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 10px;
  /* Leave room for the absolutely-positioned action slot (Set-as-next pill /
     chevron) that sits in the top-right of the outer card. */
  padding-right: ${({ $reserveActionSlot }) => ($reserveActionSlot ? '120px' : '0')};
`;

const PackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PackName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
  overflow-wrap: anywhere;
`;

type StatusColor = 'green' | 'orange' | 'red' | 'blue' | 'grey';

const statusColors = (theme: unknown, color: StatusColor) => {
  const t = r(theme);
  switch (color) {
    case 'green':
      return {
        bg: 'rgba(0, 185, 70, 0.10)',
        border: 'rgba(0, 185, 70, 0.32)',
        text: t.accent.ok,
      };
    case 'orange':
      return {
        bg: 'rgba(245, 158, 11, 0.08)',
        border: 'rgba(245, 158, 11, 0.32)',
        text: '#b45309',
      };
    case 'red':
      return {
        bg: 'rgba(255, 25, 35, 0.06)',
        border: 'rgba(255, 25, 35, 0.28)',
        text: t.accent.alert,
      };
    case 'blue':
      return {
        bg: 'rgba(0, 215, 255, 0.08)',
        border: 'rgba(0, 215, 255, 0.30)',
        text: '#0884a0',
      };
    case 'grey':
    default:
      return {
        bg: t.surface.inputIdle,
        border: t.surface.edge,
        text: t.text.muted,
      };
  }
};

const PackStatus = styled.span<{ $color: StatusColor }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme, $color }) => statusColors(theme, $color).bg};
  border: 1px solid ${({ theme, $color }) => statusColors(theme, $color).border};
  color: ${({ theme, $color }) => statusColors(theme, $color).text};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const PackDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UsageStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 390px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCell = styled.div`
  min-width: 0;
  padding: 10px 12px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
  text-align: center;
`;

const StatValue = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => r(theme).text.primary};
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const StatLabel = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-top: 4px;
`;

const NextPackBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: ${({ theme }) => r(theme).surface.raisedSelected};
  border: 1px solid ${({ theme }) => r(theme).accent.limeDeep};
  color: ${({ theme }) => r(theme).accent.ok};
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const ChevronRight = styled(RightOutline)`
  color: ${({ theme }) => r(theme).text.faint};
  font-size: 16px;
  flex-shrink: 0;
`;

const EmptyStateContainer = styled.div`
  padding: 48px 20px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => r(theme).text.muted};
  margin-bottom: 18px;
`;

const LoadingShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edge};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 0.9s linear infinite;
`;

const PackDetailPopup = styled.div`
  position: relative;
  padding: 22px 22px max(22px, env(safe-area-inset-bottom));
  background: ${({ theme }) => r(theme).surface.sheet};
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`;

const PopupHandle = styled.div`
  width: 44px;
  height: 4px;
  background: ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 2px;
  margin: 0 auto 16px;
`;

const PopupEyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 6px;
`;

const PopupTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 20px;
`;

const DetailSection = styled.section`
  margin-bottom: 18px;
`;

const DetailSectionTitle = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${r(theme).surface.edge}, transparent)`};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const DetailRow = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;

  & > span {
    display: block;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${({ theme }) => r(theme).text.faint};
    margin-bottom: 4px;
  }

  & > strong {
    display: block;
    font-family: 'Geist', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => r(theme).text.primary};
    letter-spacing: -0.005em;
    overflow-wrap: anywhere;
  }
`;

const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPackHours = (value: number | string | null | undefined): string => (
  formatValidityHours(value) ?? 'N/A'
);

const formatDate = (dateString: string | null | undefined, fallback = 'N/A'): string => {
  if (!dateString) return fallback;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return fallback;

  return date.toLocaleDateString('en-NP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPackType = (packType: string): string => {
  if (!packType) return 'Pack';
  return packType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

const sortPacksByPriority = (packs: PackHistoryItem[]): PackHistoryItem[] => {
  return [...packs].sort((a, b) => {
    const aIsDesignatedNext = a.designated_next && a.status_group === 'queued';
    const bIsDesignatedNext = b.designated_next && b.status_group === 'queued';

    if (aIsDesignatedNext && !bIsDesignatedNext) return -1;
    if (!aIsDesignatedNext && bIsDesignatedNext) return 1;

    const statusPriority: Record<string, number> = {
      active: 1,
      queued: 2,
      expired: 3,
    };

    const priorityA = statusPriority[a.status_group] || 4;
    const priorityB = statusPriority[b.status_group] || 4;
    if (priorityA !== priorityB) return priorityA - priorityB;

    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

const getStatusConfig = (
  pack: PackHistoryItem,
): { color: StatusColor; label: string; icon: React.ReactNode } => {
  const normalizedStatus = pack.status.toLowerCase();

  if (pack.status_group === 'active') {
    return {
      color: 'green',
      label: 'Active',
      icon: <CheckCircleOutline style={{ fontSize: '11px' }} />,
    };
  }

  if (pack.status_group === 'queued') {
    return {
      color: normalizedStatus === 'queued' ? 'orange' : 'blue',
      label: normalizedStatus === 'queued' ? 'Queued' : 'Reserved',
      icon: <ClockCircleOutline style={{ fontSize: '11px' }} />,
    };
  }

  if (normalizedStatus === 'depleted') {
    return {
      color: 'red',
      label: pack.kind === 'scooter' ? 'Depleted' : 'Consumed',
      icon: <ExclamationCircleOutline style={{ fontSize: '11px' }} />,
    };
  }

  if (normalizedStatus === 'consumed') {
    return {
      color: 'red',
      label: 'Consumed',
      icon: <ExclamationCircleOutline style={{ fontSize: '11px' }} />,
    };
  }

  return {
    color: 'grey',
    label: normalizedStatus === 'cancelled' ? 'Cancelled' : 'Expired',
    icon: <ExclamationCircleOutline style={{ fontSize: '11px' }} />,
  };
};

const getKindFromSearch = (searchParams: URLSearchParams): PackHistoryKind =>
  searchParams.get('type') === 'scooter' ? 'scooter' : 'energy';

const PackHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const selectedScooter = useAppSelector(selectCurrentScooter);
  const walletBalance = useAppSelector(selectWalletBalance);
  const userType = useAppSelector(selectUserType);
  const isFleetUser = isFleetType(userType);
  // Scooter packs are fleet-only; individual riders never have access,
  // so we both hide the segmented control and force the active kind back to
  // 'energy' even if the URL carries ?type=scooter.
  const activeKind: PackHistoryKind = isFleetUser
    ? getKindFromSearch(searchParams)
    : 'energy';

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<PackHistoryResponse | null>(null);
  const [activeTab, setActiveTab] = useState<PackHistoryStatusFilter>('all');
  const [selectedPack, setSelectedPack] = useState<PackHistoryItem | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [noScooterSelected, setNoScooterSelected] = useState(false);
  const [showEnergyPackModal, setShowEnergyPackModal] = useState(false);
  const [showScooterPackModal, setShowScooterPackModal] = useState(false);

  const fetchPacks = useCallback(async () => {
    if (!selectedScooter) return;

    setNoScooterSelected(false);
    setLoading(true);
    try {
      const result = await packService.getPackHistory({
        scooterVin: selectedScooter.vin,
        kind: 'all',
        status: 'all',
        limit: 200,
      });
      setHistory(result);
    } catch (error) {
      console.error('Error fetching pack history:', error);
      Toast.show({
        content: 'Failed to load pack history',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedScooter]);

  const loadUserScooters = useCallback(async () => {
    try {
      const result = await dispatch(fetchScootersAction()).unwrap();

      if (result && result.length > 0) {
        dispatch(setSelectedScooter(result[0].vin));
      } else {
        setNoScooterSelected(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading user scooters:', error);
      setNoScooterSelected(true);
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedScooter) {
      fetchPacks();
    } else {
      loadUserScooters();
    }
  }, [fetchPacks, loadUserScooters, selectedScooter]);

  // Make sure wallet balance is loaded — the empty-state purchase modals
  // surface the rider's current balance and gate affordability.
  useEffect(() => {
    if (!walletBalance) {
      dispatch(fetchWalletBalance());
    }
  }, [dispatch, walletBalance]);

  const handleEnergyPurchaseSuccess = () => {
    setShowEnergyPackModal(false);
    dispatch(fetchWalletBalance());
    fetchPacks();
  };

  const handleScooterPurchaseSuccess = () => {
    setShowScooterPackModal(false);
    dispatch(fetchWalletBalance());
    fetchPacks();
  };

  const activeCounts = history?.counts?.[activeKind] || EMPTY_COUNTS;

  const filteredPacks = useMemo(() => {
    const kindItems = history?.items.filter(item => item.kind === activeKind) || [];
    const statusItems =
      activeTab === 'all'
        ? kindItems
        : kindItems.filter(item => item.status_group === activeTab);

    return sortPacksByPriority(statusItems);
  }, [activeKind, activeTab, history]);

  // getPackHistory is requested with selectedScooter.vin, so this scan is
  // intentionally single-VIN scoped. If this page becomes a cross-VIN view,
  // this derivation must be keyed by VIN before hiding Activate Now.
  const hasActivePackByKind = useMemo<Record<PackHistoryKind, boolean>>(() => ({
    energy: Boolean(history?.items.some(item => item.kind === 'energy' && item.status_group === 'active')),
    scooter: Boolean(history?.items.some(item => item.kind === 'scooter' && item.status_group === 'active')),
  }), [history]);

  const updatePackType = (kind: PackHistoryKind) => {
    const nextParams = new URLSearchParams(searchParams);

    if (kind === 'energy') {
      nextParams.delete('type');
    } else {
      nextParams.set('type', 'scooter');
    }

    setActiveTab('all');
    setSelectedPack(null);
    setDetailVisible(false);
    setSearchParams(nextParams);
  };

  const showPackDetails = (pack: PackHistoryItem) => {
    setSelectedPack(pack);
    setDetailVisible(true);
  };

  const handleSetAsNext = async (e: React.MouseEvent, pack: PackHistoryItem) => {
    e.stopPropagation();

    try {
      if (pack.kind === 'energy') {
        if (pack.designated_next) {
          await packService.clearPackDesignation(pack.pack_id);
          Toast.show({
            content: 'Pack designation removed',
            position: 'bottom',
          });
        } else {
          await packService.designateNextPack(pack.pack_id);
          Toast.show({
            content: 'Pack set as next to activate',
            position: 'bottom',
          });
        }
      } else {
        if (pack.designated_next) {
          Toast.show({
            content: 'Pack is already next to activate',
            position: 'bottom',
          });
        } else {
          await scooterPackService.designateNextPack(pack.pack_id);
          Toast.show({
            content: 'Pack set as next to activate',
            position: 'bottom',
          });
        }
      }

      fetchPacks();
    } catch (error) {
      console.error('Error updating pack designation:', error);
      Toast.show({
        content: 'Failed to update pack designation',
        position: 'bottom',
      });
    }
  };

  const handleActivatePack = async (e: React.MouseEvent, pack: PackHistoryItem) => {
    e.stopPropagation();

    if (!canManuallyActivatePack(pack, hasActivePackByKind[pack.kind])) return;

    try {
      await activateQueuedPack(pack, {
        activateEnergyPack: packId => packService.activatePack(packId),
        activateScooterPack: packId => scooterPackService.activatePack(packId),
        invalidateReadiness: vin => invalidateScooterReadiness(vin),
      });
      Toast.show({
        content: 'Pack activated',
        position: 'bottom',
      });
      await fetchPacks();
    } catch (error) {
      console.error('Error activating pack:', error);
      Toast.show({
        content: error instanceof Error && error.message
          ? error.message
          : 'Failed to activate pack',
        position: 'bottom',
      });
    }
  };

  const calculateEnergyUsagePercentage = (pack: PackHistoryItem): number => {
    const limit = toNumber(pack.energy_limit_ah);
    if (!limit) return 0;
    return Math.min(100, Math.round((toNumber(pack.energy_used_ah) / limit) * 100));
  };

  const renderMetricRow = (pack: PackHistoryItem) => {
    const price = toNumber(pack.price_npr);

    if (pack.kind === 'energy') {
      const limit = toNumber(pack.energy_limit_ah);
      const used = toNumber(pack.energy_used_ah);
      const remaining = Math.max(limit - used, 0);

      return (
        <UsageStats>
          <StatCell>
            <StatValue>{limit ? `${formatAh(remaining)} / ${limit.toFixed(1)} Ah` : 'N/A'}</StatValue>
            <StatLabel>
              Energy Remaining{limit ? ` (${calculateEnergyUsagePercentage(pack)}% used)` : ''}
            </StatLabel>
          </StatCell>
          <StatCell>
            <StatValue>
              {pack.hours_remaining !== null && pack.hours_remaining !== undefined
                ? formatRemainingHours(pack.hours_remaining)
                : pack.validity_hours
                  ? formatPackHours(pack.validity_hours)
                  : 'N/A'}
            </StatValue>
            <StatLabel>
              {pack.hours_remaining !== null && pack.hours_remaining !== undefined
                ? 'Time Remaining'
                : 'Validity'}
            </StatLabel>
          </StatCell>
          <StatCell>
            <StatValue>{walletService.formatCurrency(price)}</StatValue>
            <StatLabel>Price</StatLabel>
          </StatCell>
        </UsageStats>
      );
    }

    const distanceLimit = toNumber(pack.distance_limit_km);
    const distanceUsed = toNumber(pack.distance_used_km);
    const distanceRemaining = toNumber(pack.distance_remaining_km);
    const hasTimeMetric =
      pack.hours_remaining !== null && pack.hours_remaining !== undefined
      || Boolean(pack.validity_hours);

    return (
      <UsageStats>
        <StatCell>
          <StatValue>
            {distanceLimit
              ? `${distanceUsed.toFixed(1)} / ${distanceLimit.toFixed(1)} km`
              : 'N/A'}
          </StatValue>
          <StatLabel>Distance Used</StatLabel>
        </StatCell>
        <StatCell>
          <StatValue>{distanceLimit ? `${distanceRemaining.toFixed(1)} km` : 'N/A'}</StatValue>
          <StatLabel>Distance Left</StatLabel>
        </StatCell>
        {hasTimeMetric && (
          <StatCell>
            <StatValue>
              {pack.hours_remaining !== null && pack.hours_remaining !== undefined
                ? formatRemainingHours(pack.hours_remaining)
                : formatPackHours(pack.validity_hours)}
            </StatValue>
            <StatLabel>
              {pack.hours_remaining !== null && pack.hours_remaining !== undefined
                ? 'Time Remaining'
                : 'Validity'}
            </StatLabel>
          </StatCell>
        )}
        <StatCell>
          <StatValue>{walletService.formatCurrency(price)}</StatValue>
          <StatLabel>Price</StatLabel>
        </StatCell>
      </UsageStats>
    );
  };

  const renderPackItem = (pack: PackHistoryItem) => {
    const statusConfig = getStatusConfig(pack);
    const isQueued = pack.status_group === 'queued';
    const isDesignatedNext = pack.designated_next && isQueued;
    const canActivateNow = canManuallyActivatePack(pack, hasActivePackByKind[pack.kind]);
    const surfaceAriaLabel = `Open pack details: ${pack.template_name}, ${statusConfig.label}`;

    return (
      <PackItem key={`${pack.kind}-${pack.pack_id}`}>
        <PackItemSurface
          aria-label={surfaceAriaLabel}
          onClick={() => showPackDetails(pack)}
        >
          <PackHeader $reserveActionSlot={!isQueued}>
            <PackInfo>
              <PackName>
                {pack.template_name}
                {isDesignatedNext && (
                  <NextPackBadge>
                    <StarFill style={{ fontSize: '9px' }} />
                    Next
                  </NextPackBadge>
                )}
              </PackName>
              <PackStatus $color={statusConfig.color}>
                {statusConfig.icon}
                {statusConfig.label}
              </PackStatus>
            </PackInfo>
          </PackHeader>

          <PackDetails>
            <DetailItem>
              <CalendarOutline style={{ fontSize: '12px' }} />
              Purchased {formatDate(pack.created_at)}
            </DetailItem>
            {pack.activated_at && (
              <DetailItem>
                <ClockCircleOutline style={{ fontSize: '12px' }} />
                Activated {formatDate(pack.activated_at)}
              </DetailItem>
            )}
          </PackDetails>

          {renderMetricRow(pack)}
        </PackItemSurface>

        {isQueued ? (
          <QueuedPackActions
            packName={pack.template_name}
            isDesignatedNext={isDesignatedNext}
            canActivateNow={canActivateNow}
            onActivate={e => handleActivatePack(e, pack)}
            onToggleNext={e => handleSetAsNext(e, pack)}
          />
        ) : (
          <PackItemActionSlot>
            <ChevronRight aria-hidden />
          </PackItemActionSlot>
        )}
      </PackItem>
    );
  };

  const renderEmptyState = () => {
    const packLabel = activeKind === 'energy' ? 'energy packs' : 'scooter packs';
    const message = activeTab === 'all' ? `No ${packLabel}` : `No ${activeTab} ${packLabel}`;
    // Energy packs are available to all riders; scooter packs are fleet-only.
    const canPurchase =
      activeKind === 'energy' ? true : isFleetUser;
    const buyLabel =
      activeKind === 'energy' ? 'Buy energy pack' : 'Buy scooter pack';

    const openPurchase = () => {
      if (activeKind === 'energy') {
        setShowEnergyPackModal(true);
      } else {
        setShowScooterPackModal(true);
      }
    };

    return (
      <EmptyStateContainer>
        <Empty
          description={<EmptyTitle>{message}</EmptyTitle>}
          image={
            <ExclamationCircleOutline
              style={{ fontSize: '40px', color: 'rgba(18, 18, 21, 0.30)' }}
            />
          }
        />
        {canPurchase && selectedScooter && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <div style={{ maxWidth: 280, width: '100%' }}>
              <PrimaryButton onClick={openPurchase}>
                <span>{buyLabel}</span>
                <ButtonChevron>→</ButtonChevron>
              </PrimaryButton>
            </div>
          </div>
        )}
      </EmptyStateContainer>
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderText>
          <PageHeaderEyebrow>Pack · History</PageHeaderEyebrow>
          <PageHeaderTitle>My packs</PageHeaderTitle>
        </PageHeaderText>
      </PageHeader>

      {isFleetUser && (
        <KindSegmented>
          <KindSegment
            $active={activeKind === 'energy'}
            onClick={() => updatePackType('energy')}
          >
            Energy Packs
          </KindSegment>
          <KindSegment
            $active={activeKind === 'scooter'}
            onClick={() => updatePackType('scooter')}
          >
            Scooter Packs
          </KindSegment>
        </KindSegmented>
      )}

      <StatusFilters>
        <StatusFilter
          $active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
        >
          All <small>({activeCounts.all})</small>
        </StatusFilter>
        <StatusFilter
          $active={activeTab === 'active'}
          onClick={() => setActiveTab('active')}
        >
          Active <small>({activeCounts.active})</small>
        </StatusFilter>
        <StatusFilter
          $active={activeTab === 'queued'}
          onClick={() => setActiveTab('queued')}
        >
          Queued <small>({activeCounts.queued})</small>
        </StatusFilter>
        <StatusFilter
          $active={activeTab === 'expired'}
          onClick={() => setActiveTab('expired')}
        >
          Expired <small>({activeCounts.expired})</small>
        </StatusFilter>
      </StatusFilters>

      {loading ? (
        <LoadingShell>
          <LoadingSpinner />
        </LoadingShell>
      ) : noScooterSelected ? (
        <EmptyStateContainer>
          <Empty
            description={<EmptyTitle>No scooter selected</EmptyTitle>}
            image={
              <ExclamationCircleOutline
                style={{ fontSize: '40px', color: 'rgba(18, 18, 21, 0.30)' }}
              />
            }
          />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <div style={{ maxWidth: 280, width: '100%' }}>
              <PrimaryButton onClick={() => navigate('/')}>
                <span>Go to home</span>
                <ButtonChevron>→</ButtonChevron>
              </PrimaryButton>
            </div>
          </div>
        </EmptyStateContainer>
      ) : (
        <PackListContainer>
          {selectedScooter && activeKind === 'energy' && activeTab === 'all' && (
            <AutoActivationToggle
              scooterVin={selectedScooter.vin}
              onPreferenceChange={() => fetchPacks()}
            />
          )}

          {filteredPacks.length === 0 ? renderEmptyState() : filteredPacks.map(renderPackItem)}
        </PackListContainer>
      )}

      <Popup
        visible={detailVisible}
        onMaskClick={() => setDetailVisible(false)}
        bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
      >
        {selectedPack && (
          <PackDetailPopup>
            <PopupHandle />
            <CloseButton onClick={() => setDetailVisible(false)}>×</CloseButton>
            <PopupEyebrow>Pack · Detail</PopupEyebrow>
            <PopupTitle>{selectedPack.template_name}</PopupTitle>

            <DetailSection>
              <DetailSectionTitle>Overview</DetailSectionTitle>
              <DetailGrid>
                <DetailRow>
                  <span>Pack ID</span>
                  <strong>{selectedPack.pack_id.slice(0, 12)}…</strong>
                </DetailRow>
                <DetailRow>
                  <span>Status</span>
                  <strong>{getStatusConfig(selectedPack).label}</strong>
                </DetailRow>
                <DetailRow>
                  <span>Type</span>
                  <strong>{formatPackType(selectedPack.pack_type)}</strong>
                </DetailRow>
                <DetailRow>
                  <span>Price</span>
                  <strong>
                    {walletService.formatCurrency(toNumber(selectedPack.price_npr))}
                  </strong>
                </DetailRow>
              </DetailGrid>
            </DetailSection>

            <DetailSection>
              <DetailSectionTitle>Timeline</DetailSectionTitle>
              <DetailGrid>
                <DetailRow>
                  <span>Purchased</span>
                  <strong>{formatDate(selectedPack.created_at)}</strong>
                </DetailRow>
                <DetailRow>
                  <span>Activated</span>
                  <strong>{formatDate(selectedPack.activated_at, 'Not activated')}</strong>
                </DetailRow>
                {selectedPack.expires_at && (
                  <DetailRow>
                    <span>Expires</span>
                    <strong>{formatDate(selectedPack.expires_at)}</strong>
                  </DetailRow>
                )}
              </DetailGrid>
            </DetailSection>

            {((selectedPack.hours_remaining !== null && selectedPack.hours_remaining !== undefined)
              || selectedPack.validity_hours) && (
              <DetailSection>
                <DetailSectionTitle>Time</DetailSectionTitle>
                <DetailGrid>
                  {selectedPack.hours_remaining !== null
                    && selectedPack.hours_remaining !== undefined && (
                      <DetailRow>
                        <span>Time Remaining</span>
                        <strong>{formatRemainingHours(selectedPack.hours_remaining)}</strong>
                      </DetailRow>
                  )}
                  {selectedPack.validity_hours ? (
                    <DetailRow>
                      <span>Validity</span>
                      <strong>{formatPackHours(selectedPack.validity_hours)}</strong>
                    </DetailRow>
                  ) : null}
                </DetailGrid>
              </DetailSection>
            )}

            {selectedPack.kind === 'energy' && (
              <DetailSection>
                <DetailSectionTitle>Energy Usage</DetailSectionTitle>
                <DetailGrid>
                  <DetailRow>
                    <span>Allocated</span>
                    <strong>{toNumber(selectedPack.energy_limit_ah).toFixed(1)} Ah</strong>
                  </DetailRow>
                  <DetailRow>
                    <span>Used</span>
                    <strong>{toNumber(selectedPack.energy_used_ah).toFixed(1)} Ah</strong>
                  </DetailRow>
                  <DetailRow>
                    <span>Remaining</span>
                    <strong>{formatAh(selectedPack.energy_remaining_ah)}</strong>
                  </DetailRow>
                  <DetailRow>
                    <span>Usage</span>
                    <strong>{calculateEnergyUsagePercentage(selectedPack)}%</strong>
                  </DetailRow>
                </DetailGrid>
              </DetailSection>
            )}

            {selectedPack.kind === 'scooter' && (
              <DetailSection>
                <DetailSectionTitle>Scooter Usage</DetailSectionTitle>
                <DetailGrid>
                  <DetailRow>
                    <span>Distance Limit</span>
                    <strong>{toNumber(selectedPack.distance_limit_km).toFixed(1)} km</strong>
                  </DetailRow>
                  <DetailRow>
                    <span>Distance Used</span>
                    <strong>{toNumber(selectedPack.distance_used_km).toFixed(1)} km</strong>
                  </DetailRow>
                  <DetailRow>
                    <span>Distance Remaining</span>
                    <strong>
                      {toNumber(selectedPack.distance_remaining_km).toFixed(1)} km
                    </strong>
                  </DetailRow>
                </DetailGrid>
              </DetailSection>
            )}
          </PackDetailPopup>
        )}
      </Popup>

      {selectedScooter && (
        <EnergyPackPurchaseModal
          visible={showEnergyPackModal}
          scooterVin={selectedScooter.vin}
          walletBalance={walletBalance?.current_balance || 0}
          onClose={() => setShowEnergyPackModal(false)}
          onSuccess={handleEnergyPurchaseSuccess}
        />
      )}

      {isFleetUser && selectedScooter && (
        <ScooterPackPurchaseModal
          visible={showScooterPackModal}
          scooterVin={selectedScooter.vin}
          walletBalance={walletBalance?.current_balance || 0}
          onClose={() => setShowScooterPackModal(false)}
          onSuccess={handleScooterPurchaseSuccess}
        />
      )}
    </PageContainer>
  );
};

export default PackHistoryPage;
